import React, { useState, useEffect, useRef } from 'react'
import './sidebar.css'
import Logo from '../../assets/images/Logo.jpg'
import {
  Search,
  LogOut,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  MessageSquareText,
  Users,
  Building2,
  MessageCircle,
  ShoppingCart,
  UserCog,
  UserPlus,
  FileText,
  Briefcase,
  Calendar,
  CalendarClock,
  ClipboardCheck,
  MapPin,
  UserCheck,
  Boxes,
  Shapes,
  HandCoins,
  Presentation,
  PackageCheck,
  Pencil,
  Shield,
  ClipboardList,
  DoorOpen,
  Wallet,
  Receipt,
  ScrollText,
  FilePlus,
  BarChart3,
  CalendarRange,
  CalendarPlus,
  LineChart,
  Tags,
  Package,
  Tag,
  Workflow
} from 'lucide-react'
import { useSidebar } from '../../context/sidebar/SidebarContext'
import { Link, useNavigate } from 'react-router-dom'
import menuItems from './menuData'
import { showToast } from '../ToastNotification/ToastNotification'
import useUserScreens from '../../hooks/useUserScreens'

const iconMap = {
  LayoutDashboard,
  MessageSquareText,
  Users,
  Building2,
  MessageCircle,
  ShoppingCart,
  UserCog,
  UserPlus,
  FileText,
  Briefcase,
  Calendar,
  CalendarClock,
  ClipboardCheck,
  MapPin,
  UserCheck,
  Boxes,
  Shapes,
  HandCoins,
  Presentation,
  PackageCheck,
  Pencil,
  Shield,
  ClipboardList,
  DoorOpen,
  Wallet,
  Receipt,
  ScrollText,
  FilePlus,
  BarChart3,
  CalendarRange,
  CalendarPlus,
  LineChart,
  Tags,
  Package,
  Tag,
  Workflow
}

const Sidebar = () => {
  const { isSidebarOpen, closeSidebar, toggleSidebar } = useSidebar()
  const [activeItems, setActiveItems] = useState(new Set())
  const [activeSubItems, setActiveSubItems] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const { screens } = useUserScreens()
  const allowedScreenIds = new Set((screens || []).map(s => s.screen_id))
  const sidebarRef = useRef(null)
  const searchContainerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = event => {
      // Don't close if clicking inside search container
      if (searchContainerRef.current && searchContainerRef.current.contains(event.target)) {
        return
      }

      // Close if clicking outside sidebar
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (closeSidebar) {
          closeSidebar()
        }
      }
    }

    if (isSidebarOpen) {
      const timerId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true)
        document.addEventListener('touchstart', handleClickOutside, true)
      }, 100)

      return () => {
        clearTimeout(timerId)
        document.removeEventListener('mousedown', handleClickOutside, true)
        document.removeEventListener('touchstart', handleClickOutside, true)
      }
    }
  }, [isSidebarOpen, closeSidebar])

  const filterMenuByScreens = (items, screenSet) => {
    return items
      .map(item => {
        if (item.subItems && Array.isArray(item.subItems)) {
          const filteredSubItems = filterMenuByScreens(item.subItems, screenSet)
          if (filteredSubItems.length > 0) {
            return { ...item, subItems: filteredSubItems }
          }
          return null
        }
        // item.id can be a single screen id or an array of ids where
        // having ANY one of them grants access (e.g. Walk-In: [13, 14, 15])
        const requiredIds = Array.isArray(item.id) ? item.id : [item.id]
        const hasAccess = requiredIds.some(id => screenSet.has(id))
        return hasAccess ? item : null
      })
      .filter(Boolean)
  }

  const menuAfterScreenFilter = filterMenuByScreens(menuItems, allowedScreenIds)

  const filterMenuBySearch = (items, search) => {
    if (!search) return items
    return items
      .map(item => {
        if (item.subItems && Array.isArray(item.subItems)) {
          const filteredSubItems = filterMenuBySearch(item.subItems, search)
          if (
            filteredSubItems.length > 0 ||
            item.label.toLowerCase().includes(search.toLowerCase())
          ) {
            return { ...item, subItems: filteredSubItems }
          }
          return null
        }
        return item.label.toLowerCase().includes(search.toLowerCase()) ? item : null
      })
      .filter(Boolean)
  }
  const filteredMenuItems = filterMenuBySearch(menuAfterScreenFilter, searchTerm)

  useEffect(() => {
    setActiveItems(new Set())
    setActiveSubItems(new Set())
  }, [searchTerm])

  const toggleItem = itemId => {
    if (searchTerm === '') {
      setActiveItems(prev => {
        const newActive = new Set()
        if (!prev.has(itemId)) {
          newActive.add(itemId)
        }
        return newActive
      })
      setActiveSubItems(new Set())
    }
  }

  const toggleSubItem = (e, subItemId, parentId) => {
    e.stopPropagation()
    if (searchTerm === '') {
      setActiveItems(prev => {
        const newActive = new Set(prev)
        newActive.add(parentId)
        return newActive
      })
      setActiveSubItems(prev => {
        const newActive = new Set()
        if (!prev.has(subItemId)) {
          newActive.add(subItemId)
        }
        return newActive
      })
    }
  }

  const handleLinkClick = (e, item) => {
    const isMobile = window.innerWidth <= 768
    const hasNoChildren = !item.subItems || item.subItems.length === 0

    if (isMobile && hasNoChildren) {
      toggleSidebar()
    }
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchSubmit = e => {
    e.preventDefault()
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    showToast('success', 'Logged out successfully')
    navigate('/login')
  }

  const renderMenuItems = (items, parentId = null) => {
    return items.map(item => {
      const Icon = iconMap[item.icon]
      const hasSubItems = item.subItems && item.subItems.length > 0
      const isExpanded = searchTerm !== '' || activeItems.has(item.id)
      const labelMatchesSearch =
        searchTerm !== '' && item.label.toLowerCase().includes(searchTerm.toLowerCase())

      return (
        <li
          key={item.id}
          className={`nav-text ${hasSubItems ? 'has-submenu' : ''} ${
            isExpanded ? 'submenu-open' : ''
          }`}
        >
          {hasSubItems ? (
            <button
              className="menu-button"
              onClick={e => {
                e.preventDefault()
                toggleItem(item.id)
              }}
              aria-expanded={isExpanded}
            >
              <div className="menu-item-content">
                <div className="menu-item-left">
                  {Icon && <Icon size={20} />}
                  <span className={labelMatchesSearch ? 'search-match' : ''}>{item.label}</span>
                </div>

                <div className="chevron-icon">
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>
            </button>
          ) : (
            <Link
              to={item.url || '#'}
              className="menu-link"
              onClick={e => handleLinkClick(e, item)}
            >
              <div className="menu-item-content">
                <div className="menu-item-left">
                  {Icon && <Icon size={20} />}
                  <span className={labelMatchesSearch ? 'search-match' : ''}>{item.label}</span>
                </div>

                <div className="chevron-icon"></div>
              </div>
            </Link>
          )}
          {hasSubItems && isExpanded && (
            <ul className={`sub-menu ${isExpanded ? 'show' : ''}`}>
              {item.subItems.map(subItem => {
                const SubIcon = iconMap[subItem.icon]
                const hasNestedItems = subItem.subItems && subItem.subItems.length > 0
                const isSubExpanded = searchTerm !== '' || activeSubItems.has(subItem.id)
                const subLabelMatchesSearch =
                  searchTerm !== '' &&
                  subItem.label.toLowerCase().includes(searchTerm.toLowerCase())

                return (
                  <li
                    key={subItem.id}
                    className={`nav-text ${hasNestedItems ? 'has-submenu' : ''} ${
                      isSubExpanded ? 'submenu-open' : ''
                    }`}
                  >
                    {hasNestedItems ? (
                      <button
                        className="menu-button"
                        onClick={e => toggleSubItem(e, subItem.id, item.id)}
                        aria-expanded={isSubExpanded}
                      >
                        <div className="menu-item-content">
                          <div className="menu-item-left">
                            {SubIcon && <SubIcon size={20} />}
                            <span className={subLabelMatchesSearch ? 'search-match' : ''}>
                              {subItem.label}
                            </span>
                          </div>
                          <div className="chevron-icon">
                            {isSubExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </div>
                        </div>
                      </button>
                    ) : (
                      <Link
                        to={subItem.url || '#'}
                        className="menu-link"
                        onClick={e => handleLinkClick(e, subItem)}
                      >
                        <div className="menu-item-content">
                          <div className="menu-item-left">
                            {SubIcon && <SubIcon size={20} />}
                            <span className={subLabelMatchesSearch ? 'search-match' : ''}>
                              {subItem.label}
                            </span>
                          </div>

                          <div className="chevron-icon"></div>
                        </div>
                      </Link>
                    )}
                    {hasNestedItems && isSubExpanded && (
                      <ul className={`sub-menu ${isSubExpanded ? 'show' : ''}`}>
                        {renderMenuItems(subItem.subItems, subItem.id)}
                      </ul>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </li>
      )
    })
  }

  return (
    <>
      <div ref={sidebarRef} className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <Link to="/" className="logo-link">
            <img src={Logo} alt="Logo" className="logo" />
          </Link>
          <div ref={searchContainerRef} className="search-container">
            <Search className="search-icon" size={20} />
            <input
              type="search"
              placeholder="Search..."
              className="search-bar"
              value={searchTerm}
              onChange={handleSearchChange}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              aria-label="Search menu items"
              maxLength="100"
            />
          </div>
        </div>

        <nav className="nav-menu">
          <ul className="nav-menu-items">
            {filteredMenuItems.length > 0
              ? renderMenuItems(filteredMenuItems)
              : searchTerm && <li className="no-results">No results found.</li>}
          </ul>
        </nav>
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="menu-link logout-button">
            <div className="menu-item-content">
              <LogOut size={20} />
              <span>Logout</span>
              <div className="chevron-icon"></div>
            </div>
          </button>
        </div>
      </div>
    </>
  )
}

export default Sidebar
